import {
  AllocationApi,
  AllocationApiConfirmAllocationPaymentRequest,
  AllocationApiGetAllocationPaymentDetailsRequest,
  AllocationApiGetAllocationPaymentTargetAccountsRequest,
  AllocationApiSubmitAllocationPaymentRequest,
  Configuration,
  ConfirmAllocationPaymentResponse,
  ConfirmDepositPaymentResponse,
  ConfirmTransferPaymentResponse,
  DepositApi,
  DepositApiConfirmDepositPaymentRequest,
  DepositApiGetDepositPaymentDetailsRequest,
  DepositApiGetDepositPaymentTargetAccountsRequest,
  DepositApiSubmitDepositPaymentRequest,
  SubmitAllocationPayment200Response,
  SubmitDepositPayment200Response,
  SubmitTransferPayment200Response,
  TransferApi,
  TransferApiConfirmTransferPaymentRequest,
  TransferApiGetTransferPaymentDetailsRequest,
  TransferApiGetTransferPaymentTargetAccountsRequest,
  TransferApiSubmitTransferPaymentRequest,
} from '@eih-netbroker/api-payment';
import { AxiosInstance } from 'axios';
import { IConfigAPI } from '../config/config';
import { ResourceHeadersWithToken } from '../services';
import { BaseResource } from './base.resource';

// Deposit
export type GetDepositPaymentDetailsRequest = Omit<
  DepositApiGetDepositPaymentDetailsRequest,
  keyof ResourceHeadersWithToken
>;

export type GetDepositPaymentTargetAccountsRequest = Omit<
  DepositApiGetDepositPaymentTargetAccountsRequest,
  keyof ResourceHeadersWithToken
>;

export type ConfirmDepositPaymentRequest = Omit<DepositApiConfirmDepositPaymentRequest, keyof ResourceHeadersWithToken>;

export type SubmitDepositPaymentRequest = Omit<DepositApiSubmitDepositPaymentRequest, keyof ResourceHeadersWithToken>;

// Transfer
export type GetTransferPaymentDetailsRequest = Omit<
  TransferApiGetTransferPaymentDetailsRequest,
  keyof ResourceHeadersWithToken
>;

export type GetTransferPaymentTargetAccountsRequest = Omit<
  TransferApiGetTransferPaymentTargetAccountsRequest,
  keyof ResourceHeadersWithToken
>;

export type ConfirmTransferPaymentRequest = Omit<
  TransferApiConfirmTransferPaymentRequest,
  keyof ResourceHeadersWithToken
>;

export type SubmitTransferPaymentRequest = Omit<
  TransferApiSubmitTransferPaymentRequest,
  keyof ResourceHeadersWithToken
>;

// Allocation
export type GetAllocationPaymentDetailsRequest = Omit<
  AllocationApiGetAllocationPaymentDetailsRequest,
  keyof ResourceHeadersWithToken
>;

export type GetAllocationPaymentTargetAccountsRequest = Omit<
  AllocationApiGetAllocationPaymentTargetAccountsRequest,
  keyof ResourceHeadersWithToken
>;

export type ConfirmAllocationPaymentRequest = Omit<
  AllocationApiConfirmAllocationPaymentRequest,
  keyof ResourceHeadersWithToken
>;

export type SubmitAllocationPaymentRequest = Omit<
  AllocationApiSubmitAllocationPaymentRequest,
  keyof ResourceHeadersWithToken
>;

export type SubmitCashActionResponse =
  | SubmitAllocationPayment200Response
  | SubmitTransferPayment200Response
  | SubmitDepositPayment200Response;

export type ConfirmCashActionResponse =
  | ConfirmAllocationPaymentResponse
  | ConfirmTransferPaymentResponse
  | ConfirmDepositPaymentResponse;

export class PaymentResource extends BaseResource {
  // Name has to be overridden, since the build job renames it to 'r' and
  // every resource class will have the same name
  // @ts-ignore
  public static name = 'PaymentResource';

  private depositApi: DepositApi;
  private transferApi: TransferApi;
  private allocationApi: AllocationApi;

  constructor(axiosInstance: AxiosInstance, basePath: string, configApi: IConfigAPI) {
    const apiBasePath = `${basePath}${configApi.usUrl.payment}`;

    super(axiosInstance, apiBasePath, configApi);
    this.depositApi = new DepositApi(new Configuration(), apiBasePath, axiosInstance);
    this.transferApi = new TransferApi(new Configuration(), apiBasePath, axiosInstance);
    this.allocationApi = new AllocationApi(new Configuration(), apiBasePath, axiosInstance);
  }

  // Deposit
  getDepositPaymentDetails(request: GetDepositPaymentDetailsRequest, headers: ResourceHeadersWithToken) {
    return this.depositApi.getDepositPaymentDetails({ ...request, ...headers }, { baseURL: this.basePath });
  }

  getDepositPaymentTargetAccounts(
    request: GetDepositPaymentTargetAccountsRequest,
    headers: ResourceHeadersWithToken,
    abortController: AbortController,
  ) {
    return this.depositApi.getDepositPaymentTargetAccounts(
      { ...request, ...headers },
      { baseURL: this.basePath, signal: abortController.signal },
    );
  }

  confirmDepositPayment(request: ConfirmDepositPaymentRequest, headers: ResourceHeadersWithToken) {
    return this.depositApi.confirmDepositPayment({ ...request, ...headers }, { baseURL: this.basePath });
  }

  submitDepositPayment(request: SubmitDepositPaymentRequest, headers: ResourceHeadersWithToken) {
    return this.depositApi.submitDepositPayment({ ...request, ...headers }, { baseURL: this.basePath });
  }

  // Transfer
  getTransferPaymentDetails(request: GetTransferPaymentDetailsRequest, headers: ResourceHeadersWithToken) {
    return this.transferApi.getTransferPaymentDetails({ ...request, ...headers }, { baseURL: this.basePath });
  }

  getTransferPaymentTargetAccounts(
    request: GetTransferPaymentTargetAccountsRequest,
    headers: ResourceHeadersWithToken,
    abortController: AbortController,
  ) {
    return this.transferApi.getTransferPaymentTargetAccounts(
      { ...request, ...headers },
      { baseURL: this.basePath, signal: abortController.signal },
    );
  }

  confirmTransferPayment(request: ConfirmTransferPaymentRequest, headers: ResourceHeadersWithToken) {
    return this.transferApi.confirmTransferPayment({ ...request, ...headers }, { baseURL: this.basePath });
  }

  submitTransferPayment(request: SubmitTransferPaymentRequest, headers: ResourceHeadersWithToken) {
    return this.transferApi.submitTransferPayment({ ...request, ...headers }, { baseURL: this.basePath });
  }

  // Allocation
  getAllocationPaymentDetails(request: GetAllocationPaymentDetailsRequest, headers: ResourceHeadersWithToken) {
    return this.allocationApi.getAllocationPaymentDetails({ ...request, ...headers }, { baseURL: this.basePath });
  }

  getAllocationPaymentTargetAccounts(
    request: GetAllocationPaymentTargetAccountsRequest,
    headers: ResourceHeadersWithToken,
    abortController: AbortController,
  ) {
    return this.allocationApi.getAllocationPaymentTargetAccounts(
      { ...request, ...headers },
      { baseURL: this.basePath, signal: abortController.signal },
    );
  }

  confirmAllocationPayment(request: ConfirmAllocationPaymentRequest, headers: ResourceHeadersWithToken) {
    return this.allocationApi.confirmAllocationPayment({ ...request, ...headers }, { baseURL: this.basePath });
  }

  submitAllocationPayment(request: SubmitAllocationPaymentRequest, headers: ResourceHeadersWithToken) {
    return this.allocationApi.submitAllocationPayment({ ...request, ...headers }, { baseURL: this.basePath });
  }
}
